import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Users, Megaphone, Zap, Activity, ArrowRight, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api.ts';
import { StatCard, Card } from '../components/ui/Card.tsx';
import { SkeletonCard } from '../components/ui/Skeleton.tsx';
import { CampaignStatusBadge, ChannelBadge } from '../components/ui/Badge.tsx';

interface Campaign {
  id: string;
  name: string;
  channel: string;
  status: string;
  totalRecipients: number;
  sentCount: number;
  bounceCount: number;
  pauseReason: string | null;
  createdAt: string;
}

interface EventEntry {
  id: string;
  eventType: string;
  customerId: string | null;
  source: string;
  processedAt: string | null;
  occurredAt: string;
}

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function DashboardPage() {
  const { data: customersData, isLoading: loadingCustomers } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.get('/api/v1/customers').then((r) => r.data),
  });

  const { data: campaignsRaw, isLoading: loadingCampaigns } = useQuery<Campaign[]>({
    queryKey: ['campaigns'],
    queryFn: () => api.get('/api/v1/campaigns').then((r) => r.data),
  });
  const campaigns: Campaign[] = Array.isArray(campaignsRaw) ? campaignsRaw : [];

  const { data: automationsRaw, isLoading: loadingAutomations } = useQuery<{ status: string }[]>({
    queryKey: ['automations'],
    queryFn: () => api.get('/api/v1/automations').then((r) => r.data),
  });
  const automations: { status: string }[] = Array.isArray(automationsRaw) ? automationsRaw : [];

  const { data: eventsData, isLoading: loadingEvents } = useQuery({
    queryKey: ['events-recent'],
    queryFn: () =>
      api.get('/api/v1/signals', { params: { limit: 10 } }).then((r) => r.data),
    refetchInterval: 15_000,
  });

  const runningCampaigns = campaigns.filter((c) => c.status === 'running');
  const activeAutomations = automations.filter((a) => a.status === 'active');
  const recentCampaigns = [...campaigns]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);
  const recentEvents: EventEntry[] = Array.isArray(eventsData?.items)
    ? eventsData.items
    : Array.isArray(eventsData)
    ? eventsData
    : [];

  // Delivery alerts: auto-paused + running with high bounce rate
  const deliveryAlerts = campaigns.filter((c) => {
    if (c.status === 'paused' && c.pauseReason?.startsWith('auto_')) return true;
    if (c.status === 'running' && c.sentCount > 0 && (c.bounceCount ?? 0) / c.sentCount > 0.02) return true;
    return false;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-zinc-900">Overview</h1>
        <p className="mt-0.5 text-sm text-zinc-500">Platform activity at a glance</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loadingCustomers ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <StatCard
              label="Total Customers"
              value={customersData?.total ?? 0}
              icon={<Users size={18} />}
            />
            <StatCard
              label="Running Campaigns"
              value={runningCampaigns.length}
              sub={`${campaigns.length} total`}
              icon={<Megaphone size={18} />}
            />
            <StatCard
              label="Active Automations"
              value={activeAutomations.length}
              sub={`${automations.length} total`}
              icon={<Zap size={18} />}
            />
            <StatCard
              label="Events (live)"
              value={eventsData?.total ?? recentEvents.length}
              sub="refreshes every 15s"
              icon={<Activity size={18} />}
            />
          </>
        )}
      </div>

      {/* Delivery alerts */}
      {deliveryAlerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={15} className="text-red-500" />
            <h2 className="text-sm font-semibold text-red-800">
              Delivery alerts ({deliveryAlerts.length})
            </h2>
          </div>
          <div className="space-y-2">
            {deliveryAlerts.map((c) => {
              const bounceRate = c.sentCount > 0 ? (c.bounceCount ?? 0) / c.sentCount : 0;
              const isAutoPaused = c.status === 'paused' && c.pauseReason?.startsWith('auto_');
              return (
                <Link
                  key={c.id}
                  to={`/campaigns/${c.id}`}
                  className="flex items-center justify-between gap-3 bg-white border border-red-100 rounded-lg px-4 py-2.5 hover:border-red-300 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-800 truncate">{c.name}</p>
                    <p className="text-xs text-red-500 mt-0.5">
                      {isAutoPaused
                        ? `Auto-paused: ${c.pauseReason === 'auto_bounce_rate' ? 'bounce rate exceeded 5%' : 'complaint rate exceeded 0.1%'}`
                        : `Bounce rate ${(bounceRate * 100).toFixed(1)}% — approaching threshold`}
                    </p>
                  </div>
                  <CampaignStatusBadge status={c.status} pauseReason={c.pauseReason} />
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Two-column content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Campaigns */}
        <Card padding="none">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
            <h2 className="text-sm font-medium text-zinc-900">Recent Campaigns</h2>
            <Link
              to="/campaigns"
              className="text-xs text-zinc-400 hover:text-zinc-700 flex items-center gap-1 transition-colors"
            >
              View all <ArrowRight size={11} />
            </Link>
          </div>

          {loadingCampaigns ? (
            <div className="p-5 space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-10 bg-zinc-50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : recentCampaigns.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-zinc-400">
              No campaigns yet
            </div>
          ) : (
            <ul className="divide-y divide-zinc-50">
              {recentCampaigns.map((c) => (
                <li key={c.id} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-zinc-50/60 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-800 truncate">{c.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <ChannelBadge channel={c.channel} />
                      <span className="text-xs text-zinc-400">{formatRelative(c.createdAt)}</span>
                    </div>
                  </div>
                  <CampaignStatusBadge status={c.status} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Recent Events */}
        <Card padding="none">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
            <h2 className="text-sm font-medium text-zinc-900">Recent Events</h2>
            <Link
              to="/events"
              className="text-xs text-zinc-400 hover:text-zinc-700 flex items-center gap-1 transition-colors"
            >
              Explorer <ArrowRight size={11} />
            </Link>
          </div>

          {loadingEvents ? (
            <div className="p-5 space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-8 bg-zinc-50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : recentEvents.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-zinc-400">
              No events yet
            </div>
          ) : (
            <ul className="divide-y divide-zinc-50">
              {recentEvents.slice(0, 8).map((ev) => (
                <li key={ev.id} className="px-5 py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <code className="text-xs font-mono text-zinc-700 truncate block">
                      {ev.eventType}
                    </code>
                    <p className="text-xs text-zinc-400 truncate">
                      {ev.customerId ? `customer: ${ev.customerId.slice(0, 8)}…` : ev.source}
                    </p>
                  </div>
                  <span className="text-xs text-zinc-400 flex-shrink-0">
                    {formatRelative(ev.occurredAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
